/**
 * 案件 (商品) の URL から同一ドメインの複数ページを読み取り、案件名・説明文・ブランド等を提案する機能の
 * API・型・採用ロジック (docs/features/product-url-crawl-summary-plan.md §4.6・§4.7・§5.3)。
 *
 * - `POST /jobs/product-url-summary {url, product_id?, hint_title?}` で非同期ジョブを投入し、`GET /jobs/<id>` で追跡する。
 *   完了の result_data は backend (status.py) が本機能専用に整形したもの (proposal / sources / brand_match / pages / screenshot_url / notes / identity)
 * - 提案は **項目ごとに採用** する。未入力項目は既定で採用、入力済み項目は既定で据え置き (判定は提案を反映する時点のフォーム値)。
 *   ただしカテゴリ・商品種別は利用者が管理する分類なので、未入力でも既定では採用しない (URL_SUMMARY_OPT_IN_FIELDS)
 * - `attributes` はキー単位でマージし、`source_pages` は採用して保存するときだけ書く (この計算は純粋関数にしてテストする)
 * - 処理中は読み込み予定ページの一覧を見せ、ページの除外と「ここまででOK」を `POST /jobs/<id>/product-url-summary/control` で指示できる (§4.4b)
 */
import type { AxiosError } from 'axios';

import { brandConfig } from '../../config/brands';
import { apiClient } from '../../services/apiClient';
import { AsyncJobError, waitForJobCompletion, type AsyncJob, type CreateJobResponse } from '../../services/asyncJobApi';
import type { IndustryCategory } from './industry';
import { INDUSTRY_CATEGORIES } from './industry';

// ============================================================
// API
// ============================================================

export interface ProductUrlSummaryJobBody {
  url: string;
  product_id?: string | null;
  hint_title?: string | null;
  /** 画面に表示した1回のポイント数。サーバーの現在の料金と違えば409 (PRICE_CHANGED) */
  quoted_credits: number;
}

/** 料金 (credit_rate_config) の行。1回30pt (計画 §14.2) */
export const URL_SUMMARY_CREDIT_RATE = { service_type: 'product_url_summary', resource_type: 'product-url-summary' } as const;

/** 料金一覧から1回のポイント数を取る (未設定・0以下は null = 受け付けられない)。 */
export function urlSummaryCreditsPerRun(
  rates: ReadonlyArray<{ service_type: string; resource_type: string; credits_per_unit: number }> | undefined,
): number | null {
  const rate = rates?.find((r) => r.service_type === URL_SUMMARY_CREDIT_RATE.service_type && r.resource_type === URL_SUMMARY_CREDIT_RATE.resource_type);
  const credits = rate ? Number(rate.credits_per_unit) : NaN;
  return Number.isFinite(credits) && credits > 0 ? credits : null;
}

export async function createProductUrlSummaryJob(body: ProductUrlSummaryJobBody): Promise<CreateJobResponse & { host?: string }> {
  const response = await apiClient.post<CreateJobResponse & { host?: string }>('/jobs/product-url-summary', body);
  return response.data;
}

// ------------------------------------------------------------
// 動画からの読み取り (video-editor-presentation-import-plan.md §4.6・段階4)
// 動画エディタに取り込み済みの収録動画 (素材起点の編集プロジェクト) の文字起こし・原稿・意味解析から、
// URL 読み取りと同じ形の提案を作る。ジョブ種別は product_video_summary、結果の形は同じなので同じパネルで採用する
// ------------------------------------------------------------

/** 読み取りの元: URL (従来) か、取り込み済みの収録動画 (動画エディタの素材起点プロジェクト) */
export type UrlSummarySource = { kind: 'url'; url: string } | { kind: 'video'; projectId: string; label: string };
export type UrlSummarySourceKind = UrlSummarySource['kind'];

export interface ProductVideoSummaryJobBody {
  project_id: string;
  product_id?: string | null;
  hint_title?: string | null;
  /** 画面に表示した1回のポイント数。サーバーの現在の料金と違えば409 (PRICE_CHANGED) */
  quoted_credits: number;
}

/** 料金 (credit_rate_config) の行。1回 (migration 20261104_04) */
export const VIDEO_SUMMARY_CREDIT_RATE = { service_type: 'product_video_summary', resource_type: 'product-video-summary' } as const;

export function videoSummaryCreditsPerRun(
  rates: ReadonlyArray<{ service_type: string; resource_type: string; credits_per_unit: number }> | undefined,
): number | null {
  const rate = rates?.find((r) => r.service_type === VIDEO_SUMMARY_CREDIT_RATE.service_type && r.resource_type === VIDEO_SUMMARY_CREDIT_RATE.resource_type);
  const credits = rate ? Number(rate.credits_per_unit) : NaN;
  return Number.isFinite(credits) && credits > 0 ? credits : null;
}

/** 読み取りの元の種類に応じた 1 回のポイント数 */
export function summaryCreditsPerRun(
  rates: ReadonlyArray<{ service_type: string; resource_type: string; credits_per_unit: number }> | undefined,
  kind: UrlSummarySourceKind,
): number | null {
  return kind === 'video' ? videoSummaryCreditsPerRun(rates) : urlSummaryCreditsPerRun(rates);
}

export async function createProductVideoSummaryJob(body: ProductVideoSummaryJobBody): Promise<CreateJobResponse & { source?: { project_id: string; label: string | null } }> {
  const response = await apiClient.post<CreateJobResponse & { source?: { project_id: string; label: string | null } }>('/jobs/product-video-summary', body);
  return response.data;
}

/** 元の種類に応じた中止 API (規則は同じ: 実行前なら返金・実行中は返金なし) */
export async function cancelProductSummary(jobId: string, kind: UrlSummarySourceKind): Promise<{ success: boolean; mode: 'cancelled' | 'interrupt'; refunded: boolean }> {
  if (kind === 'video') {
    const response = await apiClient.post<{ success: boolean; mode: 'cancelled' | 'interrupt'; refunded: boolean }>(`/jobs/${jobId}/product-video-summary/cancel`, {});
    return response.data;
  }
  return cancelProductUrlSummary(jobId);
}

/** 読み取り結果の見出しに出す元の名前 (「example.jp」/「動画「デモ収録」」) */
export function summarySourceLabel(source: UrlSummarySource | null | undefined, fallbackUrl: string): string {
  if (source?.kind === 'video') return `動画「${source.label}」`;
  return hostOfUrl(source?.kind === 'url' ? source.url : fallbackUrl);
}

/** 読み込み予定ページの除外と「ここまででOK」(§4.4b)。excluded_page_ids は除外したいページの全体を毎回送る */
export interface UrlSummaryControl {
  excluded_page_ids: number[];
  finish_fetch: boolean;
}

export async function controlProductUrlSummary(
  jobId: string,
  body: { excluded_page_ids?: number[]; finish_fetch?: true },
): Promise<UrlSummaryControl> {
  const response = await apiClient.post<UrlSummaryControl>(`/jobs/${jobId}/product-url-summary/control`, body);
  return response.data;
}

/** 利用者の「中止」: サーバー側の読み取りも打ち切る。実行前なら返金 (refunded=true)、実行中は返金なし */
export async function cancelProductUrlSummary(jobId: string): Promise<{ success: boolean; mode: 'cancelled' | 'interrupt'; refunded: boolean }> {
  const response = await apiClient.post<{ success: boolean; mode: 'cancelled' | 'interrupt'; refunded: boolean }>(`/jobs/${jobId}/product-url-summary/cancel`, {});
  return response.data;
}

export type UrlSummaryMode = 'product' | 'overview';
export type PageRelation = 'target' | 'brand_common' | 'other';

export interface UrlSummaryProposal {
  summary_mode: UrlSummaryMode;
  page_relations: Array<{ page_id: number; relation: PageRelation }>;
  title: string | null;
  description: string | null;
  brand_name: string | null;
  brand: {
    tagline: string | null;
    description: string | null;
    /** 最も確信度の高い公式サイト候補 (official_site_candidates の先頭)。無ければ null */
    official_site: string | null;
    /** 公式サイトの推定候補 (確信度の高い順・最大3件・backend validate 済み) */
    official_site_candidates: UrlSummaryBrandSiteCandidate[];
    company_name: string | null;
  };
  product_type: string | null;
  category: string[];
  industry_categories: string[];
  attributes: Record<string, string>;
  field_sources: Record<string, number[]>;
  conflicts: Array<{ field: string; values: Array<{ page_id: number; value: string }> }>;
  confidence: Record<string, 'high' | 'medium' | 'low'>;
  notes: string[];
  /** 動画からの読み取りだけ: 原稿の要点から集めた訴求候補 (採用した分を説明文の【訴求】に入れる・§4.6) */
  appeal_candidates?: string[];
  /** 商品画像として追加する候補 (読み取ったページの画像から AI が選んだもの・main が先頭・§17)。旧い結果には無い */
  image_candidates?: UrlSummaryImageCandidate[];
}

/** 商品画像の候補 1 件 (画像ファイルは取得していない。URL はそのサイトのもので、採用すると「URL指定」と同じく URL のまま枠に入る) */
export interface UrlSummaryImageCandidate {
  page_id: number;
  image_index: number;
  url: string;
  alt: string;
  width: number;
  height: number;
  /** main = 最も代表的な 1 件 (メイン画像の枠が空いていればそこへ) */
  role: 'main' | 'sub';
  /** 種別。stock (単なるイメージ写真) は backend が後ろに回している。旧い結果には無い */
  kind?: 'product' | 'visual' | 'scene' | 'logo' | 'icon' | 'portrait' | 'stock';
  reason: string;
}

/** 画像枠の形 (新規登録の下書き ProductDraftImages と、案件行の main_image_url / additional_images の共通部分) */
export interface ImageSlotsLike {
  main_image_url: string;
  /** メイン画像の名前 (画像内容の説明)。候補を入れるときは alt (無ければ選定理由) を付ける */
  main_image_name?: string | null;
  /** メイン画像の SVG 原本のパス (取り込んだ候補が SVG のとき) */
  main_image_original_path?: string | null;
  additional_images: Array<{ url: string; name?: string; original_path?: string; [key: string]: unknown }>;
}

/** 取り込み済みの候補 (url は自社ストレージの URL・SVG なら original_path 付き) */
export type ImportedImageCandidate = Pick<UrlSummaryImageCandidate, 'url'> & Partial<Pick<UrlSummaryImageCandidate, 'alt' | 'reason'>> & { original_path?: string | null };

/** 候補の名前: alt を優先し、無ければ AI の選定理由 (候補欄の見出しと同じ)。どちらも無ければ undefined */
export function imageCandidateName(candidate: Pick<UrlSummaryImageCandidate, 'alt' | 'reason'>): string | undefined {
  const name = (candidate.alt || candidate.reason || '').trim();
  return name || undefined;
}

/**
 * 候補を自社ストレージに取り込む (§17)。1 件ずつ POST し、失敗した候補は落として件数だけ返す。
 * importOne は api.importProductImageUrl (テストでは差し替え)
 */
export async function importImageCandidates(
  candidates: ReadonlyArray<UrlSummaryImageCandidate>,
  importOne: (url: string) => Promise<{ url: string; original_path?: string | null }>,
): Promise<{ imported: ImportedImageCandidate[]; failed: number }> {
  const imported: ImportedImageCandidate[] = [];
  let failed = 0;
  for (const c of candidates) {
    try {
      const r = await importOne(c.url);
      imported.push({ url: r.url, original_path: r.original_path ?? null, alt: c.alt, reason: c.reason });
    } catch {
      failed++;
    }
  }
  return { imported, failed };
}

/** 枠に入っている画像の URL (空の枠は除く) */
export function imageSlotUrls(images: Pick<ImageSlotsLike, 'main_image_url' | 'additional_images'>): string[] {
  const out: string[] = [];
  if (images.main_image_url?.trim()) out.push(images.main_image_url.trim());
  for (const img of images.additional_images) if (img?.url?.trim()) out.push(img.url.trim());
  return out;
}

/**
 * 選んだ候補を空いている枠に入れる (純関数・§17)。既存の画像は上書きしない:
 * - メイン画像が空なら先頭の候補 (main が先頭に並ぶ) をメインに
 * - 残りは追加画像の空き枠 (url が空の要素) → 末尾の順に、上限 maxAdditional 件まで
 * - 既に枠にある URL と同じ候補は入れない
 * - 入れた画像には名前 (alt、無ければ選定理由) を付ける (利用者指示 2026-11-05)
 * 戻り値の mainChanged はメイン画像を新しく入れたか (案件行の保存で原本をクリアするかの判定に使う)
 */
export function adoptImageCandidates<T extends ImageSlotsLike>(
  images: T,
  candidates: ReadonlyArray<ImportedImageCandidate>,
  maxAdditional: number,
): { images: T; added: number; skipped: number; mainChanged: boolean } {
  const present = new Set(imageSlotUrls(images));
  let main = images.main_image_url;
  let mainName: string | null | undefined = images.main_image_name;
  let mainOriginal: string | null = null;
  const additional = images.additional_images.map((img) => ({ ...img }));
  let added = 0;
  let skipped = 0;
  let mainChanged = false;
  for (const candidate of candidates) {
    const url = candidate.url.trim();
    if (!url || present.has(url)) { skipped++; continue; }
    const name = imageCandidateName({ alt: candidate.alt ?? '', reason: candidate.reason ?? '' });
    const originalPath = candidate.original_path || null;
    if (!main.trim()) {
      main = url;
      mainName = name ?? null;
      mainOriginal = originalPath;
      mainChanged = true;
    } else {
      const entry: { url: string; name?: string; original_path?: string } = { url };
      if (name) entry.name = name;
      if (originalPath) entry.original_path = originalPath;
      const empty = additional.findIndex((img) => !img.url?.trim());
      if (empty >= 0) {
        additional[empty] = entry;
      } else if (additional.length < maxAdditional) {
        additional.push(entry);
      } else {
        skipped++;
        continue;
      }
    }
    present.add(url);
    added++;
  }
  if (added === 0) return { images, added, skipped, mainChanged: false };
  return {
    images: { ...images, main_image_url: main, ...(mainChanged ? { main_image_name: mainName ?? null, main_image_original_path: mainOriginal } : {}), additional_images: additional },
    added, skipped, mainChanged,
  };
}

export interface UrlSummarySourcePage {
  page_id: number;
  url: string;
  used_for: string[];
}

export interface UrlSummaryBrandCandidate {
  brand_id: string;
  brand_name: string;
  confidence: 'high' | 'medium' | 'low';
  method: 'domain+name' | 'normalized_name' | 'domain' | 'llm';
  reason: string;
}

/** ブランドの公式サイトと推定した URL (AI の推定+読み取り元サイトのトップを low で最後に) */
export interface UrlSummaryBrandSiteCandidate {
  url: string;
  confidence: 'high' | 'medium' | 'low';
  reason: string;
}

/** ブランド新規作成モーダルに渡す初期値 (backend brand_match.new_brand_draft) */
export interface UrlSummaryNewBrandDraft {
  brand_name: string;
  tagline: string;
  description: string;
  /** ブランドコードの案 (使用済みでないことを確かめた 1 件・案が無いときや照合に失敗したときは null)。旧い結果には無い */
  brand_code?: string | null;
  /** 旧い結果には無い */
  official_site_candidates?: UrlSummaryBrandSiteCandidate[];
}

export interface UrlSummaryBrandMatch {
  status: 'matched' | 'candidate' | 'none';
  candidates: UrlSummaryBrandCandidate[];
  new_brand_draft?: UrlSummaryNewBrandDraft;
}

export interface UrlSummaryPage {
  page_id: number;
  url: string;
  final_url?: string | null;
  status: 'pending' | 'read' | 'skipped' | 'failed' | 'excluded';
  error_code?: string | null;
  relation?: PageRelation | null;
  purpose?: string | null;
  title?: string | null;
  /** 候補収集時のリンクの文言 */
  link_text?: string | null;
}

/** 完了時の result_data (backend status.py `_product_url_summary_result`)。 */
export interface UrlSummaryResult {
  proposal: UrlSummaryProposal | null;
  sources: UrlSummarySourcePage[];
  brand_match: UrlSummaryBrandMatch | null;
  pages: UrlSummaryPage[];
  root?: { url?: string; final_url?: string; status?: string; relation?: PageRelation | null };
  screenshot_url?: string | null;
  notes: string[];
  identity?: { kind?: string | null; name?: string | null; scope?: string | null };
  identity_source?: 'root' | 'reidentify' | null;
  fetched_at?: string | null;
  request?: { url?: string; project_id?: string } | null;
  /** 動画からの読み取りだけ: 元の編集プロジェクト */
  source?: { project_id?: string | null; project_name?: string | null; label?: string | null; duration_sec?: number | null } | null;
}

/** 実行中の result_data (`_product_url_summary_progress`)。 */
/** 段階の中の細かい進捗 (台帳の step。ページ取得中は「ページを開いています」等) */
export interface UrlSummaryStep {
  key?: string | null;
  text?: string | null;
  at?: string | null;
}

export interface UrlSummaryProgress {
  stage_key?: string | null;
  /** 段階ごとの状態 (backend の job_stages。完了 / 実行中 / 未実行 / スキップ / 失敗 / 中止)。縦ステッパーが使う (§8.3) */
  stages?: Array<{ key: string; status: 'done' | 'running' | 'pending' | 'skipped' | 'failed' | 'cancelled'; label?: string }>;
  step?: UrlSummaryStep | null;
  root?: { status?: string | null; error_code?: string | null };
  pages?: UrlSummaryPage[];
  notes?: string[];
  deadline_at?: string | null;
  /** 利用者の指示 (選定後〜要約開始前だけ載る) */
  control?: UrlSummaryControl | null;
}

// ============================================================
// 文言
// ============================================================

export const URL_SUMMARY_REQUEST_ERROR_MESSAGES: Record<string, string> = {
  INVALID_URL: 'このURLは取得できません。公開されているWebページのURLを指定してください',
  UNSUPPORTED_SITE: 'このサイトは読み取りに対応していません。メーカー公式サイトや商品ページのURLを指定してください',
  CONCURRENT_LIMIT: '同一組織で同時に実行できる読み取りは1件までです。進行中の読み取りが終わってからお試しください',
  DAILY_LIMIT: '本日の上限に達しました',
  PRODUCT_NOT_FOUND: '案件が見つかりません',
  PRICE_CHANGED: '料金が更新されました。ブラウザーでページ全体を再読み込みして確認してください',
  RATE_NOT_CONFIGURED: 'URL読み取りの料金が未設定のため、現在ご利用いただけません',
  PROJECT_NOT_FOUND: '動画の編集が見つかりません',
  VIDEO_NOT_READY: 'この動画はまだ取り込みが終わっていません。動画エディタで取り込みが完了してからお試しください',
  NO_SPEECH: 'この動画には読み取れる発話がありませんでした',
};

export const URL_SUMMARY_JOB_ERROR_MESSAGES: Record<string, string> = {
  LOGIN_WALL: 'このページはログインが必要か、アクセスが制限されているため読み取れません',
  ACCESS_RESTRICTED: 'このページはログインが必要か、アクセスが制限されているため読み取れません',
  NAVIGATION_BLOCKED: 'このページは別のサイトへ転送されるため読み取れません。公開されている商品ページのURLを指定してください',
  NO_CONTENT: 'ページから読み取れる情報がほとんどありませんでした',
  RENDER_INCOMPLETE: 'ページの表示が終わる前に時間切れになりました。もう一度お試しください',
  SUMMARY_FAILED: '読み取り結果をまとめられませんでした。時間をおいてお試しください',
  TIMEOUT: '読み取りに時間がかかりすぎたため中断しました。時間をおいてお試しください',
  CAPTURE_FAILED: 'ページを取得できませんでした。時間をおいてもう一度お試しください',
  STALE_PENDING: '読み取りを開始できませんでした。時間をおいてお試しください',
  DISPATCH_FAILED: '読み取りを開始できませんでした。時間をおいてお試しください',
  PROJECT_NOT_FOUND: '動画の編集が見つかりません',
  VIDEO_NOT_READY: 'この動画はまだ取り込みが終わっていません。動画エディタで取り込みが完了してからお試しください',
  NO_SPEECH: 'この動画には読み取れる発話がありませんでした',
};

export const URL_SUMMARY_STAGE_LABELS: Record<string, string> = {
  fetch_root: '指定ページを読んでいます',
  candidates: '読むページの候補を集めています',
  select: '読むページを選んでいます',
  fetch_pages: '追加ページを読んでいます',
  reidentify: '商品を特定しています',
  summarize: '読み取った内容をAIがまとめています',
  brand_match: 'ブランドを照合しています',
  done: '完了',
};

// ============================================================
// 読み込み予定ページ (§4.4b)
// ============================================================

/** 利用者が除外・打ち切りを指示できる段階 (選定後〜要約開始前) */
/** 縦ステッパーの一覧用ラベル (体言止め)。順序は backend の段階定義と同じ (テストで固定) */
export const URL_SUMMARY_STAGE_ORDER = ['fetch_root', 'candidates', 'select', 'fetch_pages', 'reidentify', 'summarize', 'brand_match', 'done'] as const;
export const URL_SUMMARY_STEPPER_LABELS: Record<string, string> = {
  fetch_root: '指定ページを読む',
  candidates: '読むページの候補を集める',
  select: '読むページを選ぶ',
  fetch_pages: '追加ページを読む',
  reidentify: '商品を特定する',
  summarize: '読み取った内容をまとめる',
  brand_match: 'ブランドを照合する',
  done: '完了',
};

/**
 * 進捗応答の stages が無い (古い backend) ときの既定: 現在の段階だけ running・前は done・後は pending。
 * 受付直後でまだ進捗が届いていない (stage_key も無い) ときは先頭の段階を running にする (全部「これから」のままだと
 * 止まって見えて戸惑うため)
 */
export const VIDEO_SUMMARY_STAGE_ORDER = ['load', 'summarize', 'brand_match', 'done'] as const;
export const VIDEO_SUMMARY_STAGE_LABELS: Record<string, string> = {
  load: '動画の文字起こしを読んでいます',
  summarize: '話された内容をAIがまとめています',
  brand_match: 'ブランドを照合しています',
  done: '完了',
};
export const VIDEO_SUMMARY_STEPPER_LABELS: Record<string, string> = {
  load: '動画の文字起こしを読む',
  summarize: '話された内容をまとめる',
  brand_match: 'ブランドを照合する',
  done: '完了',
};

export function urlSummaryStages(
  progress: UrlSummaryProgress | null | undefined,
  order: ReadonlyArray<string> = URL_SUMMARY_STAGE_ORDER,
): Array<{ key: string; status: 'done' | 'running' | 'pending' | 'skipped' | 'failed' | 'cancelled' }> {
  if (progress?.stages && progress.stages.length > 0) return progress.stages;
  const current = progress?.stage_key ?? order[0];
  let seen = false;
  return order.map((key) => {
    if (key === current) { seen = true; return { key, status: 'running' as const }; }
    return { key, status: seen ? ('pending' as const) : ('done' as const) };
  });
}

export const URL_SUMMARY_CONTROL_STAGES: ReadonlySet<string> = new Set(['fetch_pages', 'reidentify']);

export const URL_SUMMARY_PURPOSE_LABELS: Record<string, string> = {
  spec: '仕様',
  feature: '特長',
  price: '価格',
  faq: 'FAQ',
  brand: 'ブランド',
  other: 'その他',
};

export type PlannedPageState = 'waiting' | 'loading' | 'read' | 'excluded' | 'failed' | 'skipped';

export const URL_SUMMARY_PAGE_STATE_LABELS: Record<PlannedPageState, string> = {
  waiting: '待ち',
  loading: '読込中',
  read: '読了',
  excluded: '除外',
  failed: '失敗',
  skipped: '時間切れ',
};

export interface PlannedPageRow {
  pageId: number;
  state: PlannedPageState;
  /** 読了後はページタイトル、それまではリンクの文言 (無ければ URL のパス) */
  label: string;
  url: string;
  purpose: string | null;
  /** 除外ボタンを出すか (押せば除外・除外中なら戻す) */
  canToggle: boolean;
  /** 利用者が除外を指示したがワーカーがまだ反映していない、または反映済み */
  userExcluded: boolean;
}

function pathOf(url: string): string {
  try {
    const u = new URL(url);
    return `${u.pathname}${u.search}` || url;
  } catch {
    return url;
  }
}

/**
 * 処理中の進捗と手元の除外集合から、読み込み予定ページの表示行を作る。
 * 取得は順番に1件ずつなので、取得段階では「待ち」のうち除外していない先頭を「読み込み中」にする。
 * 「ここまででOK」を押した後は、残りの待ちを除外として見せる (ワーカーは読み込み中のページを読み終えてから止める)。
 */
export function buildPlannedPageRows(
  progress: UrlSummaryProgress | null | undefined,
  excluded: ReadonlySet<number>,
  finishRequested: boolean,
): PlannedPageRow[] {
  const pages = progress?.pages ?? [];
  const controllable = URL_SUMMARY_CONTROL_STAGES.has(progress?.stage_key ?? '');
  const fetching = progress?.stage_key === 'fetch_pages';
  let loadingAssigned = false;
  return pages.map((p) => {
    const userExcluded = excluded.has(p.page_id) || (p.status === 'excluded' && p.error_code === 'USER_EXCLUDED');
    let state: PlannedPageState;
    if (p.status === 'pending') {
      if (userExcluded) state = 'excluded';
      else if (fetching && !loadingAssigned) {
        state = 'loading';
        loadingAssigned = true;
      } else state = finishRequested ? 'excluded' : 'waiting';
    } else if (p.status === 'read') {
      state = userExcluded ? 'excluded' : 'read';
    } else {
      state = p.status;
    }
    const title = p.status === 'read' ? p.title?.trim() : '';
    const linkText = p.link_text?.trim();
    // ワーカーが確定させた除外 (待ちのまま除外・打ち切り) は戻せない。読了後の除外は要約まで戻せる
    const settled = p.status === 'excluded' || p.status === 'failed' || p.status === 'skipped';
    return {
      pageId: p.page_id,
      state,
      label: title || linkText || pathOf(p.url),
      url: p.final_url || p.url,
      purpose: p.purpose ? (URL_SUMMARY_PURPOSE_LABELS[p.purpose] ?? null) : null,
      // 「ここまででOK」の後の待ちは打ち切りで読まないので、除外の操作も出さない
      canToggle: controllable && !settled && !(finishRequested && p.status === 'pending' && state !== 'loading'),
      userExcluded,
    };
  });
}

/** 投入 API のエラー → 利用者向け文言 (BE の error を優先し、無ければ error_code の既定文言)。 */
export function urlSummaryRequestErrorMessage(err: unknown): string {
  const data = (err as AxiosError<{ error?: string; error_code?: string }> | undefined)?.response?.data;
  if (data && typeof data.error === 'string' && data.error.trim()) return data.error;
  if (data?.error_code && URL_SUMMARY_REQUEST_ERROR_MESSAGES[data.error_code]) return URL_SUMMARY_REQUEST_ERROR_MESSAGES[data.error_code]!;
  if (err instanceof Error && err.message && !/status code/i.test(err.message)) return err.message;
  return '読み取りを開始できませんでした';
}

/** ジョブ失敗 → 利用者向け文言 (error_code の既定文言 → ジョブの error_message → 既定)。 */
export function urlSummaryJobErrorMessage(err: unknown): string {
  if (err instanceof AsyncJobError) {
    if (err.error_code && URL_SUMMARY_JOB_ERROR_MESSAGES[err.error_code]) return URL_SUMMARY_JOB_ERROR_MESSAGES[err.error_code]!;
    if (err.message && err.message !== 'Job failed') return err.message;
  }
  if (err instanceof Error && err.name === 'AbortError') return '読み取りを中止しました';
  if (err instanceof Error && err.message && err.message !== 'Job failed') return err.message;
  return URL_SUMMARY_JOB_ERROR_MESSAGES.SUMMARY_FAILED!;
}

/** 実行中の表示 (「追加ページを読んでいます (2/6)」)。 */
export function urlSummaryProgressText(job: Pick<AsyncJob, 'status' | 'stage' | 'result_data'>, kind: UrlSummarySourceKind = 'url'): string {
  if (job.status === 'pending') return '順番待ちです';
  const rd = (job.result_data ?? {}) as UrlSummaryProgress;
  const stageKey = rd.stage_key ?? job.stage?.detail ?? '';
  const labels = kind === 'video' ? VIDEO_SUMMARY_STAGE_LABELS : URL_SUMMARY_STAGE_LABELS;
  const base = labels[stageKey] ?? job.stage?.text ?? '読み取っています';
  const details: string[] = [];
  if (stageKey === 'fetch_pages' && Array.isArray(rd.pages) && rd.pages.length > 0) {
    const done = rd.pages.filter((p) => p.status !== 'pending').length;
    details.push(`${Math.min(done + 1, rd.pages.length)}/${rd.pages.length}`);
  }
  const step = rd.step?.text?.trim();
  // まとめの段階は段階名で「AIが」と言っているので、AI への問い合わせ中 (llm) は括弧で重ねない (利用者指示 2026-10-30)
  const redundant = stageKey === 'summarize' && rd.step?.key === 'llm';
  if (step && !redundant) details.push(step);
  return details.length > 0 ? `${base} (${details.join('・')})` : base;
}

/** 経過時間の表示 (「45秒」「1分20秒」「1時間5分」) */
export function formatUrlSummaryElapsed(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const sec = total % 60;
  if (h > 0) return `${h}時間${m}分`;
  if (m > 0) return `${m}分${sec}秒`;
  return `${sec}秒`;
}

/** 投入からの経過で出す注意 (§5.5: 10分で「時間がかかっています」、2時間で「完了しませんでした」)。 */
export const URL_SUMMARY_SLOW_AFTER_MS = 10 * 60 * 1000;
export const URL_SUMMARY_GIVE_UP_AFTER_MS = 2 * 60 * 60 * 1000;
export const URL_SUMMARY_SLOW_NOTICE = '時間がかかっています。しばらくしてから結果を確認してください';
export const URL_SUMMARY_GIVE_UP_NOTICE = '読み取りが完了しませんでした。新しく読み取りを始めてください';

// ============================================================
// 処理中ジョブの引き継ぎ (ページを離れて戻ったときに続きをポーリングして結果を表示する)
// ============================================================

/** 案件単位 (新規登録は 'new') で保持する、処理中または結果未採用の読み取りジョブ */
export interface UrlSummaryResume {
  jobId: string;
  /** URL 読み取りの URL。動画からの読み取りでは空文字 (source に元を持つ) */
  url: string;
  /** 投入時刻 (ms)。10分 / 2時間の注意と経過表示の起点 */
  startedAt: number;
  /** 動画からの読み取りだけ (旧い保存には無い = URL) */
  source?: UrlSummarySource;
}

function parseResumeSource(raw: unknown): UrlSummarySource | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const s = raw as Partial<Record<'kind' | 'url' | 'projectId' | 'label', unknown>>;
  if (s.kind === 'video' && typeof s.projectId === 'string' && s.projectId) return { kind: 'video', projectId: s.projectId, label: typeof s.label === 'string' ? s.label : '' };
  if (s.kind === 'url' && typeof s.url === 'string' && s.url) return { kind: 'url', url: s.url };
  return undefined;
}

/** 保持の上限。成果物は7日で消えるが、古い結果を黙って出し直すのは避けて24時間で捨てる */
export const URL_SUMMARY_RESUME_MAX_AGE_MS = 24 * 60 * 60 * 1000;
const RESUME_KEY_PREFIX = 'productUrlSummary.resume:';

/**
 * 保存キーの持ち主。同じブラウザで別のアカウント (別の組織) にログインしたとき、前の利用者の読み取りを
 * 復元表示したり、権限エラーで前の利用者の引き継ぎ情報を消したりしないよう、ユーザーと組織の識別子で分ける
 */
export interface UrlSummaryResumeScope {
  userUid: string;
  organizationId: string | null;
}

type ResumeStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

function resumeStorage(): ResumeStorage | null {
  try {
    return typeof window !== 'undefined' && window.localStorage ? window.localStorage : null;
  } catch {
    return null;
  }
}

export function urlSummaryResumeKey(scope: UrlSummaryResumeScope, productId?: string | null): string {
  return `${RESUME_KEY_PREFIX}${scope.userUid}:${scope.organizationId || '-'}:${productId || 'new'}`;
}

export function loadUrlSummaryResume(
  scope: UrlSummaryResumeScope,
  productId?: string | null,
  options?: { now?: number; storage?: ResumeStorage | null },
): UrlSummaryResume | null {
  const storage = options?.storage === undefined ? resumeStorage() : options.storage;
  if (!storage) return null;
  try {
    const raw = storage.getItem(urlSummaryResumeKey(scope, productId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<UrlSummaryResume>;
    if (typeof parsed.jobId !== 'string' || !parsed.jobId || typeof parsed.url !== 'string' || typeof parsed.startedAt !== 'number') return null;
    const now = options?.now ?? Date.now();
    if (now - parsed.startedAt > URL_SUMMARY_RESUME_MAX_AGE_MS) return null;
    const source = parseResumeSource(parsed.source);
    return { jobId: parsed.jobId, url: parsed.url, startedAt: parsed.startedAt, ...(source ? { source } : {}) };
  } catch {
    return null;
  }
}

export function saveUrlSummaryResume(
  scope: UrlSummaryResumeScope,
  productId: string | null | undefined,
  resume: UrlSummaryResume,
  storage: ResumeStorage | null = resumeStorage(),
): void {
  try {
    storage?.setItem(urlSummaryResumeKey(scope, productId), JSON.stringify(resume));
  } catch {
    // 保存できなくても読み取り自体は続ける (引き継ぎだけ利かない)
  }
}

export function clearUrlSummaryResume(scope: UrlSummaryResumeScope, productId: string | null | undefined, storage: ResumeStorage | null = resumeStorage()): void {
  try {
    storage?.removeItem(urlSummaryResumeKey(scope, productId));
  } catch {
    // 何もしない
  }
}

export async function waitForProductUrlSummary(
  jobId: string,
  options?: { onProgress?: (job: AsyncJob) => void; signal?: AbortSignal; pollInterval?: number },
): Promise<UrlSummaryResult> {
  const job = await waitForJobCompletion<UrlSummaryResult>(jobId, {
    pollInterval: options?.pollInterval ?? 3000,
    maxDuration: URL_SUMMARY_GIVE_UP_AFTER_MS,
    skipVisibilityCheck: true,
    onProgress: options?.onProgress,
    signal: options?.signal,
  });
  return job.result_data;
}

// ============================================================
// 採用ロジック (純粋関数)
// ============================================================

/** 提案を反映する時点のフォーム値 (判定に使う)。category はカンマ区切りの文字列 (フォームと同じ)。 */
export interface UrlSummaryFormSnapshot {
  title: string;
  description: string;
  brand_name: string;
  brand_id: string;
  product_type: string;
  category: string;
  industry_categories: IndustryCategory[];
}

export type UrlSummaryTextField = 'title' | 'description' | 'brand_name' | 'product_type' | 'category' | 'industry_categories';
export const URL_SUMMARY_TEXT_FIELDS: readonly UrlSummaryTextField[] = ['title', 'description', 'brand_name', 'product_type', 'category', 'industry_categories'];
export const URL_SUMMARY_FIELD_LABELS: Record<UrlSummaryTextField, string> = {
  title: brandConfig.labels.productName,
  description: '説明',
  brand_name: 'ブランド名',
  product_type: `${brandConfig.labels.productShort}種別`,
  category: 'カテゴリ',
  industry_categories: '特定カテゴリ',
};

export interface UrlSummaryFieldItem {
  key: string; // 'title' | ... | 'attributes.<key>'
  label: string;
  current: string;
  proposed: string | null;
  /** 既定の採用 (反映時点のフォーム値が空で、提案値があり、矛盾が無い) */
  adopt: boolean;
  /** 提案値が現在の値と同じ (採用しても変わらないので採用させない) */
  unchanged: boolean;
  conflict: Array<{ page_id: number; value: string }> | null;
  confidence: 'high' | 'medium' | 'low' | null;
  sources: number[];
}

function proposedText(proposal: UrlSummaryProposal, field: UrlSummaryTextField): string | null {
  switch (field) {
    case 'category':
      return proposal.category.length > 0 ? proposal.category.join(', ') : null;
    case 'industry_categories':
      return proposal.industry_categories.length > 0 ? proposal.industry_categories.join(',') : null;
    default: {
      const v = proposal[field];
      return typeof v === 'string' && v.trim() ? v : null;
    }
  }
}

function currentText(form: UrlSummaryFormSnapshot, field: UrlSummaryTextField): string {
  if (field === 'industry_categories') return form.industry_categories.join(',');
  return (form[field] ?? '').toString();
}

/** 現在の値と提案値が同じか。空白の揺れは無視し、カンマ区切りの分類項目は順不同で比べる */
function sameValue(field: UrlSummaryTextField, current: string, proposed: string | null): boolean {
  if (proposed == null) return false;
  if (field === 'category' || field === 'industry_categories') {
    const norm = (v: string) => v.split(',').map((x) => x.trim()).filter(Boolean).sort().join(',');
    return norm(current) !== '' && norm(current) === norm(proposed);
  }
  const norm = (v: string) => v.replace(/\s+/g, ' ').trim();
  return norm(current) !== '' && norm(current) === norm(proposed);
}

/** 提案と現在値から項目一覧 (既定の採用つき) を作る。反映時点のフォーム値で判定する (ジョブ中の手入力は据え置き)。 */
/**
 * 提案があっても既定では採用しない項目。カテゴリ・商品種別はマニュアル登録では利用者が案件を管理しやすいように
 * 自分で付ける分類なので、提案は参考として出すだけにし、採用は利用者の明示的なチェックに任せる
 */
export const URL_SUMMARY_OPT_IN_FIELDS: ReadonlySet<UrlSummaryTextField> = new Set<UrlSummaryTextField>(['category', 'product_type']);

export function buildFieldItems(proposal: UrlSummaryProposal, form: UrlSummaryFormSnapshot): UrlSummaryFieldItem[] {
  const conflictsByField = new Map(proposal.conflicts.map((c) => [c.field, c.values]));
  const items: UrlSummaryFieldItem[] = [];
  for (const field of URL_SUMMARY_TEXT_FIELDS) {
    const conflict = conflictsByField.get(field) ?? null;
    const proposed = proposedText(proposal, field);
    const current = currentText(form, field);
    if (proposed == null && !conflict) continue;
    items.push({
      key: field,
      label: URL_SUMMARY_FIELD_LABELS[field],
      current,
      proposed,
      adopt: !conflict && proposed != null && current.trim() === '' && !URL_SUMMARY_OPT_IN_FIELDS.has(field),
      unchanged: !conflict && sameValue(field, current, proposed),
      conflict,
      confidence: proposal.confidence[field] ?? null,
      sources: proposal.field_sources[field] ?? [],
    });
  }
  for (const [key, value] of Object.entries(proposal.attributes)) {
    const fieldKey = `attributes.${key}`;
    items.push({
      key: fieldKey,
      label: `補足: ${key}`,
      current: '',
      proposed: value,
      adopt: true,
      unchanged: false,
      conflict: null,
      confidence: proposal.confidence[fieldKey] ?? null,
      sources: proposal.field_sources[fieldKey] ?? [],
    });
  }
  for (const c of proposal.conflicts) {
    if (!c.field.startsWith('attributes.') || items.some((i) => i.key === c.field)) continue;
    items.push({ key: c.field, label: `補足: ${c.field.slice('attributes.'.length)}`, current: '', proposed: null, adopt: false, unchanged: false, conflict: c.values, confidence: null, sources: [] });
  }
  return items;
}

/** 採用する項目から、フォームに入れる値と attributes の差分を作る。矛盾のある項目は chosen で選ばれた値を使う。 */
export interface UrlSummaryAdoption {
  fields: Partial<Pick<UrlSummaryFormSnapshot, 'title' | 'description' | 'brand_name' | 'product_type' | 'category'>> & { industry_categories?: IndustryCategory[] };
  attributes: Record<string, string>;
  /** 採用した項目 (source_pages.fields の対象) */
  adoptedKeys: string[];
}

export function buildAdoption(
  items: UrlSummaryFieldItem[],
  adopted: ReadonlySet<string>,
  chosen: ReadonlyMap<string, string> = new Map(),
): UrlSummaryAdoption {
  const fields: UrlSummaryAdoption['fields'] = {};
  const attributes: Record<string, string> = {};
  const adoptedKeys: string[] = [];
  for (const item of items) {
    if (!adopted.has(item.key)) continue;
    const value = item.conflict ? chosen.get(item.key) ?? null : item.proposed;
    if (value == null || !value.trim()) continue;
    adoptedKeys.push(item.key);
    if (item.key.startsWith('attributes.')) {
      attributes[item.key.slice('attributes.'.length)] = value;
      continue;
    }
    switch (item.key as UrlSummaryTextField) {
      case 'industry_categories':
        fields.industry_categories = value.split(',').map((s) => s.trim()).filter((s): s is IndustryCategory => (INDUSTRY_CATEGORIES as readonly string[]).includes(s));
        break;
      default:
        fields[item.key as 'title' | 'description' | 'brand_name' | 'product_type' | 'category'] = value;
    }
  }
  return { fields, attributes, adoptedKeys };
}

export const APPEALS_HEADING = '【訴求】';

/**
 * 採用した訴求候補を説明文の末尾に「【訴求】」の見出しで足す (計画 §4.6: 保存先の「訴求」項目ができるまでの置き場)。
 * 説明文を採用していれば提案の説明文に、採用していなければ現在のフォームの説明文に足し、description を採用項目に加える。
 * 訴求が 0 件ならそのまま返す
 */
export function applyAppeals(adoption: UrlSummaryAdoption, appeals: readonly string[], currentDescription: string): UrlSummaryAdoption {
  const picked = appeals.map((a) => a.trim()).filter(Boolean);
  if (picked.length === 0) return adoption;
  const block = `${APPEALS_HEADING}\n${picked.map((a) => `・${a}`).join('\n')}`;
  const base = (adoption.fields.description ?? currentDescription).trimEnd();
  const description = base ? `${base}\n\n${block}` : block;
  const adoptedKeys = adoption.adoptedKeys.includes('description') ? adoption.adoptedKeys : [...adoption.adoptedKeys, 'description'];
  return { ...adoption, fields: { ...adoption.fields, description }, adoptedKeys };
}

/**
 * 新規登録の下書きへの自動反映 (入口フロー文書 §6.3・利用者決定 2026-10-30): フォームは空なので提案は全部入れる。
 * 分類 (カテゴリ・案件種別) も入れ、食い違いは先頭の候補を入れておく (後からフォームで直せる)。
 * 現在値が同じ・既に入力済みの項目は据え置く (読み直しのとき)。
 */
export function buildFillAllAdoption(items: UrlSummaryFieldItem[]): { adopted: Set<string>; chosen: Map<string, string> } {
  const adopted = new Set<string>();
  const chosen = new Map<string, string>();
  for (const item of items) {
    if (item.unchanged || item.current.trim() !== '') continue;
    if (item.conflict) {
      const first = item.conflict.find((c) => c.value.trim())?.value;
      if (!first) continue;
      chosen.set(item.key, first);
      adopted.add(item.key);
      continue;
    }
    if (item.proposed && item.proposed.trim()) adopted.add(item.key);
  }
  return { adopted, chosen };
}

/** attributes のキー単位マージ: 採用したキーだけ上書きし、未採用と提案に無いキーは既存値を保つ。 */
export function mergeAttributes(existing: Record<string, unknown> | null | undefined, adopted: Record<string, string>): Record<string, unknown> {
  const out: Record<string, unknown> = { ...(existing ?? {}) };
  for (const [k, v] of Object.entries(adopted)) {
    if (v == null || v === '') continue; // null の提案値・空では既存値を消さない
    out[k] = v;
  }
  return out;
}

/** products.attributes.source_pages (§5.3)。採用した項目だけを、ジョブの sources から引いた URL で残す。 */
export interface SourcePages {
  root_url: string;
  urls: string[];
  fields: Record<string, string[]>;
  fetched_at: string;
  job_id: string;
}

export function buildSourcePages(
  result: Pick<UrlSummaryResult, 'sources' | 'root' | 'request' | 'fetched_at'>,
  adoptedKeys: readonly string[],
  fieldSources: Record<string, number[]>,
  jobId: string,
  now: Date = new Date(),
): SourcePages | null {
  if (adoptedKeys.length === 0) return null;
  const urlById = new Map(result.sources.map((s) => [s.page_id, s.url]));
  const fields: Record<string, string[]> = {};
  const used = new Set<string>();
  for (const key of adoptedKeys) {
    const urls = (fieldSources[key] ?? []).map((id) => urlById.get(id)).filter((u): u is string => !!u);
    if (urls.length > 0) {
      fields[key] = urls;
      urls.forEach((u) => used.add(u));
    }
  }
  const rootUrl = result.root?.final_url || result.root?.url || result.request?.url || urlById.get(0) || '';
  if (rootUrl) used.add(rootUrl);
  return {
    root_url: rootUrl,
    urls: Array.from(used),
    fields,
    fetched_at: result.fetched_at || now.toISOString(),
    job_id: jobId,
  };
}

// ============================================================
// ブランド (§4.7)
// ============================================================

export interface BrandAdoptionPlan {
  /** 既定で採用する候補 (matched かつ案件に brand_id が未設定のとき)。候補が無ければ null */
  defaultCandidate: UrlSummaryBrandCandidate | null;
  /** 表示する候補 (matched は 1 件・candidate は複数) */
  candidates: UrlSummaryBrandCandidate[];
  /** 案件に別の brand_id が入っているので据え置き (注意表示) */
  keptExisting: boolean;
  /** 候補なし → 新規作成の提案 */
  suggestCreate: boolean;
  notice: string | null;
}

export function planBrandAdoption(
  match: UrlSummaryBrandMatch | null | undefined,
  currentBrandId: string,
  brands: ReadonlyArray<{ id: string; brand_name: string }>,
): BrandAdoptionPlan {
  if (!match) return { defaultCandidate: null, candidates: [], keptExisting: false, suggestCreate: false, notice: null };
  const known = match.candidates.filter((c) => brands.some((b) => b.id === c.brand_id));
  if (match.status === 'none' || known.length === 0) {
    return { defaultCandidate: null, candidates: [], keptExisting: false, suggestCreate: match.status === 'none' || known.length === 0, notice: null };
  }
  const first = known[0]!;
  if (currentBrandId) {
    if (currentBrandId === first.brand_id) {
      return { defaultCandidate: null, candidates: known, keptExisting: false, suggestCreate: false, notice: '読み取り結果は選択中のブランドと一致しています' };
    }
    const label = brands.find((b) => b.id === first.brand_id)?.brand_name ?? first.brand_name;
    return { defaultCandidate: null, candidates: known, keptExisting: true, suggestCreate: false, notice: `読み取り結果は「${label}」でした (選択中のブランドは据え置きます)` };
  }
  if (match.status === 'matched') {
    return { defaultCandidate: first, candidates: known, keptExisting: false, suggestCreate: false, notice: `「${brands.find((b) => b.id === first.brand_id)?.brand_name ?? first.brand_name}」と一致しそうです (${first.reason})` };
  }
  return { defaultCandidate: null, candidates: known, keptExisting: false, suggestCreate: false, notice: '一致しそうなブランドの候補があります' };
}

/** brand_id と brand_name を一組で入れる (その時点のブランド一覧の名前を使う)。一覧に無ければ null。 */
export function brandPair(brandId: string, brands: ReadonlyArray<{ id: string; brand_name: string }>): { brand_id: string; brand_name: string } | null {
  const b = brands.find((x) => x.id === brandId);
  return b ? { brand_id: b.id, brand_name: b.brand_name } : null;
}

// ============================================================
// URL
// ============================================================

/** 入力欄の URL を整える (スキームが無ければ https:// を補う)。http/https 以外・ホストが無いものは null。 */
export function normalizeSummaryUrl(input: string): string | null {
  const trimmed = input.trim();
  if (!trimmed) return null;
  const withScheme = /^[a-z][a-z0-9+.-]*:/i.test(trimmed) ? trimmed : `https://${trimmed}`;
  try {
    const url = new URL(withScheme);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;
    if (!url.hostname || !url.hostname.includes('.')) return null;
    return url.toString();
  } catch {
    return null;
  }
}

export function hostOfUrl(url: string): string {
  try {
    return new URL(url).hostname;
  } catch {
    return url;
  }
}
